import { moduleById } from '../data/skillModules'
import { AudienceLevel, Cohort, LEVEL_LABELS } from '../types/pathway'

interface CsvInput {
  pathwayName: string
  company: string
  cohort: Cohort | null
  audience: AudienceLevel | 'any'
  /** Module ids in the order the prospect should take them. */
  items: string[]
  /** Optional field labels the rep ticked in the send dialog. */
  fields: string[]
}

/** Optional columns, in the order they should appear when selected. */
const OPTIONAL_COLUMNS: {
  field: string
  header: string
  value: (moduleId: string) => string
}[] = [
  {
    field: 'Category',
    header: 'Category',
    value: (id) => moduleById(id)?.category ?? '',
  },
  {
    field: 'Duration',
    header: 'Duration (hrs)',
    value: (id) => String(moduleById(id)?.durationHours ?? ''),
  },
  {
    field: 'Topics',
    header: 'Topics',
    value: (id) => moduleById(id)?.topics.join(' | ') ?? '',
  },
  {
    field: 'Skill Module ID',
    header: 'Skill Module ID',
    value: (id) => moduleById(id)?.courseId ?? '',
  },
]

const BASE_HEADERS = [
  'Pathway',
  'Prepared For',
  'Cohort',
  'Audience Level',
  'Order',
  'Skill Module',
]

const CORE_HEADERS = ['Description', 'Relevance', 'What You’ll Learn']

function escapeCell(value: string | number): string {
  const text = String(value ?? '')
  return `"${text.replace(/"/g, '""')}"`
}

export function buildPathwayCsv({
  pathwayName,
  company,
  cohort,
  audience,
  items,
  fields,
}: CsvInput): string {
  const optional = OPTIONAL_COLUMNS.filter((column) =>
    fields.includes(column.field),
  )

  const headers = [
    ...BASE_HEADERS,
    ...optional.map((column) => column.header),
    ...CORE_HEADERS,
  ]

  const rows: string[][] = []

  items.forEach((moduleId, index) => {
    const module = moduleById(moduleId)
    if (!module) return
    rows.push([
      pathwayName || 'Recommended Learning Pathway',
      company || '',
      cohort ? `${cohort.organization} — ${cohort.name}` : '',
      audience === 'any' ? 'Mixed' : LEVEL_LABELS[audience],
      String(index + 1),
      module.name,
      ...optional.map((column) => column.value(moduleId)),
      module.description,
      module.relevance,
      module.outcomes.join(' | '),
    ])
  })

  return [headers, ...rows]
    .map((row) => row.map(escapeCell).join(','))
    .join('\r\n')
}

export function csvFileName(pathwayName: string): string {
  const slug = (pathwayName || 'pathway')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `${slug || 'pathway'}-modules.csv`
}

export function downloadCsv(fileName: string, contents: string): void {
  const blob = new Blob([`\uFEFF${contents}`], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
